import React, { useState, useEffect } from "react";
import { IResourceComponentsProps } from "@pankod/refine-core";
import { 
  List, 
  Card, 
  Button, 
  Space, 
  Table, 
  Typography, 
  Tag, 
  Checkbox, 
  Modal, 
  message,
  Spin,
  Statistic,
  Row,
  Col,
  Alert,
  Tooltip,
  Input,
  Select
} from "@pankod/refine-antd";
import { SyncOutlined, ReloadOutlined, InfoCircleOutlined } from "@ant-design/icons";
import toast from "react-hot-toast";
import { getUnsyncedProducts, syncBulkProducts, rebuildCMSCache, rebuildProntoCache, invalidateProntoCache, fetchProductDetails, getBridgeSyncStatus } from "Services/productServices";

const { Text, Title } = Typography;

interface UnsyncedProduct {
  masterSku: string;
  itemCode: string;
  description: string;
  description2?: string;
  description3?: string;
  category?: string;
  class?: string;
  group?: string;
  conditionCode?: string;
  warehouses: Array<{
    code: string;
    name: string;
    availableQty: number;
  }>;
  totalAvailableQty: number;
  hasStock: boolean;
  error?: string;
}

interface UnsyncedResponse {
  message: string;
  summary: {
    prontoTotal: number;
    cmsTotal: number;
    unsyncedTotal: number;
    returned: number;
  };
  products: UnsyncedProduct[];
  timestamp: string;
}

export const UnsyncedProducts: React.FC<IResourceComponentsProps> = () => {
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [rebuildingCache, setRebuildingCache] = useState(false);
  const [rebuildingProntoCache, setRebuildingProntoCache] = useState(false);
  const [products, setProducts] = useState<UnsyncedProduct[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [summary, setSummary] = useState<UnsyncedResponse['summary'] | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [filters, setFilters] = useState({
    hasStock: undefined as boolean | undefined,
    searchText: '',
  });
  const [sortField, setSortField] = useState<keyof UnsyncedProduct>('masterSku');
  const [sortOrder, setSortOrder] = useState<'ascend' | 'descend'>('descend');
  const [isSyncActive, setIsSyncActive] = useState(false);

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const response = await getBridgeSyncStatus();
        setIsSyncActive(response.data.isSyncRunning);
      } catch (error) {
        console.error("Failed to check sync status:", error);
      }
    };

    checkStatus();
    const interval = setInterval(checkStatus, 30000); // Check every 30s
    return () => clearInterval(interval);
  }, []);

  const fetchUnsyncedProducts = async () => {
    setLoading(true);
    try {
      // Show loading toast for long operations
      const loadingToast = toast.loading('Fetching products from Pronto and CMS...');
      
      const response = await getUnsyncedProducts(500);
      const data: UnsyncedResponse = response.data;
      
      // Update loading toast with success
      toast.success(`Found ${data.summary.unsyncedTotal} unsynced products`, { id: loadingToast });
      
      setProducts(data.products);
      setSummary(data.summary);
      setLastUpdated(data.timestamp);
      setSelectedRowKeys([]);
      setHasLoadedOnce(true);
    } catch (error: any) {
      console.error('Error fetching unsynced products:', error);
      toast.error(`Failed to fetch unsynced products: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRebuildCache = async () => {
    setRebuildingCache(true);
    try {
      const loadingToast = toast.loading('Rebuilding CMS product cache...');
      
      const response = await rebuildCMSCache();
      const { productCount } = response.data;
      
      toast.success(`CMS cache rebuilt successfully! Found ${productCount} products`, { id: loadingToast });
      
      // Automatically refresh the unsynced products list after cache rebuild
      setTimeout(() => {
        fetchUnsyncedProducts();
      }, 1000);
    } catch (error: any) {
      console.error('Error rebuilding CMS cache:', error);
      toast.error(`Failed to rebuild CMS cache: ${error.message}`);
    } finally {
      setRebuildingCache(false);
    }
  };

  const handleRebuildProntoCache = async () => {
    if (isSyncActive) {
      toast.error("Cannot rebuild Pronto cache while a full sync is running. Please wait for the sync to complete.");
      return;
    }

    setRebuildingProntoCache(true);
    try {
      const loadingToast = toast.loading('Replenishing Pronto warehouse cache from fresh data...');
      
      // We don't invalidate first anymore so that the current cache 
      // remains available while the new data is being fetched
      const response = await rebuildProntoCache();
      const { itemCount } = response.data;
      
      toast.success(`Pronto cache rebuilt successfully! Found ${itemCount || 'unknown number of'} products`, { id: loadingToast });
      
      // Automatically refresh the unsynced products list after cache rebuild
      setTimeout(() => {
        fetchUnsyncedProducts();
      }, 1000);
    } catch (error: any) {
      console.error('Error rebuilding Pronto cache:', error);
      toast.error(`Failed to rebuild Pronto cache: ${error.message}`);
    } finally {
      setRebuildingProntoCache(false);
    }
  };

  // Remove the automatic fetch on mount

  const handleBulkSync = async () => {
    if (selectedRowKeys.length === 0) {
      toast.error('Please select at least one product to sync');
      return;
    }

    setSyncing(true);
    try {
      const masterSkus = selectedRowKeys.map(key => String(key));
      
      // Show progress toast for each product
      masterSkus.forEach(sku => {
        toast.loading(`Syncing ${sku}...`, { id: sku });
      });
      
      const response = await syncBulkProducts(masterSkus);
      const result = response.data;
      
      // Update toasts based on results
      result.results.forEach((r: any) => {
        if (r.status === 'success') {
          toast.success(`${r.masterSku} queued for sync`, { id: r.masterSku });
        } else {
          toast.error(`${r.masterSku} failed: ${r.error}`, { id: r.masterSku });
        }
      });
      
      // Show summary
      if (result.summary.errors > 0) {
        toast.error(`Sync completed with ${result.summary.errors} errors`);
      } else {
        toast.success(`All ${result.summary.success} products queued for sync!`);
      }
      
      setShowSyncModal(false);
      
      // Refresh the list after a short delay
      setTimeout(() => {
        fetchUnsyncedProducts();
      }, 2000);
      
    } catch (error: any) {
      console.error('Error syncing products:', error);
      toast.error(`Failed to sync products: ${error.message}`);
    } finally {
      setSyncing(false);
    }
  };

  const handleFetchDetails = async (masterSku: string) => {
    let loadingToast: string;
    try {
      loadingToast = toast.loading(`Fetching details for ${masterSku}...`);
      
      const response = await fetchProductDetails(masterSku);
      const productDetails = response.data;
      
      // Update the product in the list with fetched details
      setProducts(prev => prev.map(p => 
        p.masterSku === masterSku 
          ? { 
              ...p, 
              description: productDetails.description || p.description,
              description2: productDetails.description2 || p.description2,
              description3: productDetails.description3 || p.description3,
              category: productDetails.category || p.category,
              class: productDetails.class || p.class,
              group: productDetails.group || p.group,
              conditionCode: productDetails.conditionCode || p.conditionCode,
            }
          : p
      ));
      
      toast.success(`Details fetched for ${masterSku}`, { id: loadingToast });
    } catch (error: any) {
      console.error('Error fetching product details:', error);
      
      // Handle 404 (product not found) differently
      if (error.response?.status === 404) {
        toast.error(`Product ${masterSku} not found in Pronto. It may be an invalid SKU.`, { id: loadingToast });
        // Mark the product as having an error
        setProducts(prev => prev.map(p => 
          p.masterSku === masterSku 
            ? { 
                ...p, 
                error: 'Product not found in Pronto',
                description: 'Product not found in Pronto',
              }
            : p
        ));
      } else {
        toast.error(`Failed to fetch details for ${masterSku}: ${error.message}`, { id: loadingToast });
      }
    }
  };

  // Filter and sort products
  const filteredProducts = products
    .filter(p => {
      if (filters.hasStock !== undefined && p.hasStock !== filters.hasStock) return false;
      if (filters.searchText) {
        const searchLower = filters.searchText.toLowerCase();
        return p.masterSku.toLowerCase().includes(searchLower) ||
               p.description.toLowerCase().includes(searchLower) ||
               (p.category && p.category.toLowerCase().includes(searchLower)) ||
               (p.class && p.class.toLowerCase().includes(searchLower));
      }
      return true;
    })
    .sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];
      
      if (aValue === undefined || aValue === null) return 1;
      if (bValue === undefined || bValue === null) return -1;
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortOrder === 'ascend' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortOrder === 'ascend' ? aValue - bValue : bValue - aValue;
      }
      
      return 0;
    });

  const columns = [
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_: any, record: UnsyncedProduct) => (
        <Tooltip title="Fetch full details from Pronto">
          <Button
            size="small"
            icon={<InfoCircleOutlined />}
            onClick={() => handleFetchDetails(record.masterSku)}
            type="link"
          >
            Details
          </Button>
        </Tooltip>
      ),
    },
    {
      title: 'Master SKU',
      dataIndex: 'masterSku',
      key: 'masterSku',
      width: 120,
      sorter: true,
      sortOrder: sortField === 'masterSku' ? sortOrder : null,
      render: (sku: string) => <Text strong>{sku}</Text>,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (desc: string, record: UnsyncedProduct) => (
        <div>
          <Text>{desc}</Text>
          {record.description2 && (
            <div>
              <Text type="secondary" style={{ fontSize: '12px' }}>{record.description2}</Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 100,
      sorter: true,
      sortOrder: sortField === 'category' ? sortOrder : null,
      render: (cat: string) => cat ? <Tag>{cat}</Tag> : '-',
    },
    {
      title: 'Class',
      dataIndex: 'class',
      key: 'class',
      width: 100,
      sorter: true,
      sortOrder: sortField === 'class' ? sortOrder : null,
      render: (cls: string) => cls ? <Tag color="blue">{cls}</Tag> : '-',
    },
    {
      title: 'Stock Status',
      key: 'stock',
      width: 120,
      sorter: true,
      sortOrder: sortField === 'totalAvailableQty' ? sortOrder : null,
      render: (_: any, record: UnsyncedProduct) => (
        <Space direction="vertical" size="small">
          <Text strong>{record.totalAvailableQty} units</Text>
          <Tag color={record.hasStock ? 'green' : 'red'}>
            {record.hasStock ? 'In Stock' : 'Out of Stock'}
          </Tag>
        </Space>
      ),
    },
    {
      title: 'Warehouses',
      dataIndex: 'warehouses',
      key: 'warehouses',
      width: 150,
      render: (warehouses: UnsyncedProduct['warehouses']) => (
        <Space size="small" wrap>
          {warehouses
            .filter(w => w.availableQty > 0)
            .slice(0, 3)
            .map(w => (
              <Tag key={w.code} color="default">
                {w.code}: {w.availableQty}
              </Tag>
            ))}
          {warehouses.filter(w => w.availableQty > 0).length > 3 && (
            <Text type="secondary">+{warehouses.filter(w => w.availableQty > 0).length - 3} more</Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 100,
      render: (_: any, record: UnsyncedProduct) => (
        record.error ? (
          <Tooltip title={record.error}>
            <Tag color="red">Error</Tag>
          </Tooltip>
        ) : (
          <Tag color="orange">Not Synced</Tag>
        )
      ),
    },
  ];

  const handleTableChange = (
    pagination: any,
    filters: any,
    sorter: any,
  ) => {
    if (sorter.field) {
      setSortField(sorter.field as keyof UnsyncedProduct);
      setSortOrder(sorter.order || 'ascend');
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
    getCheckboxProps: (record: UnsyncedProduct) => ({
      disabled: !!record.error,
    }),
  };

  return (
    <div>
      <List title="Unsynced Products">
        {!hasLoadedOnce ? (
          <Card>
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Title level={4}>Find Unsynced Products</Title>
              <Text type="secondary">
                Click the button below to find products that exist in Pronto but not in the CMS.
                This process compares all products between both systems and may take a few moments.
              </Text>
              <div style={{ marginTop: 24 }}>
                <Button
                  type="primary"
                  size="large"
                  icon={<SyncOutlined />}
                  onClick={fetchUnsyncedProducts}
                  loading={loading}
                >
                  Find Unsynced Products
                </Button>
              </div>
            </div>
          </Card>
        ) : (
          <>
            <Card style={{ marginBottom: 24 }}>
              <Row gutter={16}>
                <Col span={6}>
                  <Statistic
                    title="Total Pronto Products"
                    value={summary?.prontoTotal || 0}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Total CMS Products"
                    value={summary?.cmsTotal || 0}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Unsynced Products"
                    value={summary?.unsyncedTotal || 0}
                    valueStyle={{ color: '#fa8c16' }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Products Shown"
                    value={summary?.returned || 0}
                    valueStyle={{ color: '#722ed1' }}
                  />
                </Col>
              </Row>
              {lastUpdated && (
                <div style={{ marginTop: 16 }}>
                  <Text type="secondary">
                    Last updated: {new Date(lastUpdated).toLocaleString()}
                  </Text>
                </div>
              )}
              {loading && (
                <div style={{ marginTop: 16 }}>
                  <Text type="secondary">
                    <Spin size="small" /> Fetching products from Pronto and CMS... This may take a moment.
                  </Text>
                </div>
              )}
            </Card>

            {summary && summary.prontoTotal < 100 && (
              <Alert
                message="Low Pronto Product Count Detected"
                description={`Only ${summary.prontoTotal} products found in Pronto. This seems unusually low. Click "Rebuild Pronto Cache" to fetch all products from Pronto.`}
                type="warning"
                showIcon
                style={{ marginBottom: 24 }}
                action={
                  <Button size="small" onClick={handleRebuildProntoCache} loading={rebuildingProntoCache}>
                    Rebuild Now
                  </Button>
                }
              />
            )}

            {summary && summary.cmsTotal < 100 && (
              <Alert
                message="Low CMS Product Count Detected"
                description={`Only ${summary.cmsTotal} products found in CMS. This seems unusually low. Click "Rebuild CMS Cache" to fetch all products from CMS.`}
                type="warning"
                showIcon
                style={{ marginBottom: 24 }}
                action={
                  <Button size="small" onClick={handleRebuildCache} loading={rebuildingCache}>
                    Rebuild Now
                  </Button>
                }
              />
            )}

            {isSyncActive && (
              <Alert
                message="Bridge Sync is currently running"
                description="A full product sync is currently in progress in the ProntoBridge. To avoid resource conflicts, 'Rebuild Pronto Cache' is temporarily disabled."
                type="warning"
                showIcon
                style={{ marginBottom: 24 }}
              />
            )}

            {summary && summary.unsyncedTotal > 0 && (
              <Alert
                message="Products Found"
                description={`Found ${summary.unsyncedTotal} products in Pronto that are not in the CMS. Select products and click "Sync Selected" to add them to the CMS.`}
                type="info"
                showIcon
                style={{ marginBottom: 24 }}
              />
            )}

            <Card
              title="Unsynced Products"
              extra={
                <Space>
                  <Tooltip title={isSyncActive ? "Rebuild is disabled while a bridge sync is in progress" : "Rebuilds the Pronto warehouse cache from scratch. Use this if Pronto product count seems too low or empty."}>
                    <Button
                      icon={<ReloadOutlined />}
                      onClick={handleRebuildProntoCache}
                      loading={rebuildingProntoCache}
                      type="default"
                      disabled={isSyncActive}
                    >
                      Rebuild Pronto Cache
                    </Button>
                  </Tooltip>
                  <Tooltip title="Rebuilds the CMS product cache from scratch. Use this if CMS product count seems too low.">
                    <Button
                      icon={<ReloadOutlined />}
                      onClick={handleRebuildCache}
                      loading={rebuildingCache}
                      type="default"
                    >
                      Rebuild CMS Cache
                    </Button>
                  </Tooltip>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={fetchUnsyncedProducts}
                    loading={loading}
                  >
                    Refresh
                  </Button>
                  <Button
                    type="primary"
                    icon={<SyncOutlined />}
                    onClick={() => setShowSyncModal(true)}
                    disabled={selectedRowKeys.length === 0}
                  >
                    Sync Selected ({selectedRowKeys.length})
                  </Button>
                </Space>
              }
            >
              <Space style={{ marginBottom: 16 }} wrap>
                <Input.Search
                  placeholder="Search by SKU, description, category, or class"
                  value={filters.searchText}
                  onChange={(e) => setFilters(prev => ({ ...prev, searchText: e.target.value }))}
                  style={{ width: 300 }}
                  allowClear
                />
                <Select
                  placeholder="Filter by stock"
                  value={filters.hasStock}
                  onChange={(value) => setFilters(prev => ({ ...prev, hasStock: value }))}
                  style={{ width: 150 }}
                  allowClear
                >
                  <Select.Option value={true}>In Stock</Select.Option>
                  <Select.Option value={false}>Out of Stock</Select.Option>
                </Select>
                <Text type="secondary">
                  Showing {filteredProducts.length} of {products.length} products
                </Text>
              </Space>
              <Spin spinning={loading}>
                <Table
                  rowKey="masterSku"
                  columns={columns}
                  dataSource={filteredProducts}
                  rowSelection={rowSelection}
                  onChange={handleTableChange}
                  pagination={{
                    total: filteredProducts.length,
                    pageSize: 50,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) =>
                      `${range[0]}-${range[1]} of ${total} products`,
                  }}
                  scroll={{ x: 1200 }}
                />
              </Spin>
            </Card>
          </>
        )}
      </List>

      <Modal
        title="Confirm Bulk Sync"
        visible={showSyncModal}
        onOk={handleBulkSync}
        onCancel={() => setShowSyncModal(false)}
        confirmLoading={syncing}
        okText="Yes, sync these products"
        cancelText="Cancel"
      >
        <p>Are you sure you want to sync <strong>{selectedRowKeys.length}</strong> products?</p>
        <p>This will queue each selected product to be refreshed from Pronto and created/updated in the CMS.</p>
        <p>The sync process runs in the background, so it may take a few minutes to complete.</p>
      </Modal>
    </div>
  );
};
